import React, { useState, useEffect, useRef } from "react";
import {
  Clock,
  Paperclip,
  Plus,
  Trash2,
  FileText,
  Download,
  X,
  Upload,
  AlertCircle,
  Maximize2,
  Minimize2,
  Moon,
  Sun,
  Users,
  Filter,
  SortAsc,
  Calendar,
  FileType2,
  RefreshCcw,
  ArrowUpDown,
  Search,
  ChevronDown,
  ChevronUp,
  Shield
} from "lucide-react";
import { createClient } from "@supabase/supabase-js";
import { toast } from "react-hot-toast";
import { formatDistanceToNow, format } from "date-fns";
import { tr } from "date-fns/locale";
import { FiPaperclip, FiX, FiLoader, FiFile, FiEdit, FiRefreshCw } from "react-icons/fi";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";
import { useTheme } from "../../context/ThemeContext";
// Import notification service
import { createNotification } from "../../lib/notificationService";

// Simple custom Button component 
const Button = ({ 
  children, 
  type = "button", 
  variant = "primary", 
  size = "md", 
  disabled = false, 
  className = "", 
  onClick = () => {} 
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case "outline":
        return "border border-gray-300 dark:border-gray-600 bg-transparent text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700";
      case "ghost":
        return "bg-transparent text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700";
      default:
        return "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800";
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "py-1 px-2 text-sm";
      case "lg":
        return "py-3 px-6 text-lg";
      default:
        return "py-2 px-4 text-base";
    }
  };

  return (
    <button
      type={type}
      disabled={disabled}
      className={`rounded-md transition-colors duration-200 font-medium flex items-center justify-center ${getVariantClasses()} ${getSizeClasses()} ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

// Supabase istemcisini oluştur
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
);

// Dosya işlemleri için ayrı Supabase client oluştur
const fileSupabase = createClient(
  import.meta.env.VITE_FILE_SUPABASE_URL,
  import.meta.env.VITE_FILE_SUPABASE_ANON_KEY
);

const TaskNotes = ({ task }) => {
  const { theme, toggleTheme } = useTheme();
  const [noteText, setNoteText] = useState("");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [editFileToRemove, setEditFileToRemove] = useState(false);
  const [editNewFile, setEditNewFile] = useState(null);
  const editFileInputRef = useRef(null);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, hasFile, edited
  const [filterUser, setFilterUser] = useState('all');
  const [filterDate, setFilterDate] = useState('all'); // all, today, week, month
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, user
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  // New state for form visibility
  const [isFormVisible, setIsFormVisible] = useState(true);
  const formRef = useRef(null);

  // Mevcut kullanıcı bilgilerini al
  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await supabase
          .from("user_profiles")
          .select("*")
          .eq("id", user.id)
          .single();
        setCurrentUser(userProfile);
      }
    };
    getCurrentUser();
  }, []);

  // Görev notlarını getir
  useEffect(() => {
    if (task) {
      fetchTaskNotes();
    }
  }, [task?.id]);

  // Fetch task participants for notifications
  const [taskParticipants, setTaskParticipants] = useState([]);
  
  useEffect(() => {
    if (task?.id) {
      fetchTaskParticipants();
    }
  }, [task?.id]);
  
  const fetchTaskParticipants = async () => {
    try {
      // Get assigned members from the task
      const { data: taskData, error: taskError } = await supabase
        .from("tasks")
        .select("assigned_members, created_by")
        .eq("id", task.id)
        .single();
        
      if (taskError) throw taskError;
      
      // Collect unique participants
      const participants = new Set();
      
      // Add task creator
      if (taskData.created_by) {
        participants.add(taskData.created_by);
      }
      
      // Add assigned members
      if (taskData.assigned_members) {
        const assignedMembers = typeof taskData.assigned_members === 'string' 
          ? JSON.parse(taskData.assigned_members) 
          : taskData.assigned_members;
          
        assignedMembers.forEach(member => {
          const memberId = member.id || member;
          participants.add(memberId);
        });
      }
      
      setTaskParticipants(Array.from(participants));
    } catch (error) {
      console.error("Task participants fetch error:", error);
    }
  };

  const fetchTaskNotes = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("task_notes")
        .select("*")
        .eq("task_id", task.id)
        .order("created_at", { ascending: false });
  
      if (error) throw error;
  
      // Kullanıcı bilgilerini ayrı fetch et
      const notesWithUsers = await Promise.all(
        data.map(async (note) => {
          const { data: userData } = await supabase
            .from("user_profiles")
            .select("id, first_name, last_name, profile_photo_url")
            .eq("id", note.user_id)
            .single();
  
          return {
            ...note,
            user_profiles: userData
          };
        })
      );
  
      setNotes(notesWithUsers);
    } catch (error) {
      console.error("Notlar yüklenirken hata oluştu:", error);
      toast.error("Notlar yüklenirken bir hata oluştu");
    } finally {
      setIsLoading(false);
    }
  };
  

  // Dosya ekle butonuna tıklandığında gizli dosya input'unu tetikle
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Dosya seçildiğinde çalışacak fonksiyon
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Dosya boyutu kontrolü (maksimum 128 GB)
    if (file.size > 128 * 1024 * 1024 * 1024) {
      toast.error("Dosya boyutu 128GB'tan büyük olamaz!");
      return;
    }
    
    // Seçilen dosyayı state'e kaydet
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Not içeriği veya dosya en az birinin olması gerekir
    if (!noteText.trim() && !selectedFile) {
      toast.warning("Lütfen not ekleyin veya dosya seçin");
      return;
    }
    
    setLoading(true);
    
    try {
      let fileData = null;
      
      // Dosya varsa önce yükle
      if (selectedFile) {
        fileData = await uploadFile(selectedFile, task.id);
      }
      
      // Not verisini oluştur
      const noteData = {
        task_id: task.id,
        user_id: currentUser.id,
        content: noteText.trim(),
        created_at: new Date().toISOString(),
      };
      
      // Eğer dosya yüklendiyse dosya bilgilerini ekle
      if (fileData) {
        noteData.attachment_url = fileData.url;
        noteData.attachment_name = fileData.name;
        noteData.attachment_size = fileData.size;
        noteData.attachment_url = fileData.path;
      }
      
      // Not verisini Supabase'e kaydet
      const { data, error } = await supabase
        .from("task_notes")
        .insert(noteData)
        .select()
        .single();
      
      if (error) throw error;
      
      // Kullanıcı bilgilerini ekle
      const newNote = {
        ...data,
        user_profiles: {
          first_name: currentUser.first_name,
          last_name: currentUser.last_name,
          profile_photo_url: currentUser.profile_photo_url
        }
      };
      
      // Notlar listesini güncelle
      setNotes(prevNotes => [newNote, ...prevNotes]);
      
      // Send notifications to participants
      await notifyNoteAdded(data, currentUser.id);
      
      // Formu temizle
      setNoteText("");
      setSelectedFile(null);
      
      toast.success("Not başarıyla eklendi");
    } catch (error) {
      console.error("Not eklenirken hata oluştu:", error);
      toast.error("Not eklenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Notify participants when a note is added
  const notifyNoteAdded = async (noteData, excludeUserId) => {
    try {
      // Get current user's name
      const userName = `${currentUser.first_name} ${currentUser.last_name}`;
      
      // Notify each participant except the note creator
      for (const userId of taskParticipants) {
        if (userId !== excludeUserId) {
          await createNotification(
            userId,
            excludeUserId,
            'Yeni Görev Notu',
            `${userName} "${task.title}" görevine yeni bir not ekledi.`,
            'task',
            task.id,
            `/tasks/${task.id}`,
            {
              task_title: task.title,
              note_id: noteData.id,
              has_attachment: !!noteData.attachment_url,
              notification_subtype: 'task_note_added'
            }
          );
        }
      }
    } catch (error) {
      console.error("Notification error:", error);
    }
  };
  
  // Notify participants when a note is updated
  const notifyNoteUpdated = async (noteData, excludeUserId) => {
    try {
      // Get current user's name
      const userName = `${currentUser.first_name} ${currentUser.last_name}`;
      
      // Notify each participant except the note updater
      for (const userId of taskParticipants) {
        if (userId !== excludeUserId) {
          await createNotification(
            userId,
            excludeUserId,
            'Görev Notu Güncellendi',
            `${userName} "${task.title}" görevindeki bir notu güncelledi.`,
            'task',
            task.id,
            `/tasks/${task.id}`,
            {
              task_title: task.title,
              note_id: noteData.id,
              has_attachment: !!noteData.attachment_url,
              notification_subtype: 'task_note_updated'
            }
          );
        }
      }
    } catch (error) {
      console.error("Notification error:", error);
    }
  };
  
  // Notify participants when a note is deleted
  const notifyNoteDeleted = async (noteId, excludeUserId) => {
    try {
      // Get current user's name
      const userName = `${currentUser.first_name} ${currentUser.last_name}`;
      
      // Notify each participant except the person who deleted the note
      for (const userId of taskParticipants) {
        if (userId !== excludeUserId) {
          await createNotification(
            userId,
            excludeUserId,
            'Görev Notu Silindi',
            `${userName} "${task.title}" görevinden bir notu sildi.`,
            'task',
            task.id,
            `/tasks/${task.id}`,
            {
              task_title: task.title,
              notification_subtype: 'task_note_deleted'
            }
          );
        }
      }
    } catch (error) {
      console.error("Notification error:", error);
    }
  };

  // Not silme fonksiyonu
  const handleDeleteNote = async (noteId) => {
    try {
      const noteToDelete = notes.find(note => note.id === noteId);
      
      // Yetki kontrolü - sadece not sahibi veya süper kullanıcı silebilir
      if (currentUser.id !== noteToDelete.user_id && currentUser.role !== 'super') {
        toast.error("Bu notu silmek için yetkiniz bulunmuyor");
        return;
      }
      
      // Doğru bucket adını kullan
      const bucketName = import.meta.env.VITE_FILE_SUPABASE_BUCKET_NAME;
      
      // Önce notu getir
      const { data: noteData, error: fetchError } = await supabase
        .from("task_notes")
        .select("attachment_url, attachment_url")
        .eq("id", noteId)
        .single();

      if (fetchError) throw fetchError;

      // Eğer ekte bir dosya varsa, önce storage'dan sil
      if (noteData?.attachment_url) {
        try {
          // Dosya yolunu doğrudan attachment_url'den al
          const filePath = noteData.attachment_url;
          
          if (!filePath) {
            console.warn("Dosya yolu bulunamadı");
            return;
          }

          const { error: storageError } = await fileSupabase.storage
            .from(bucketName)
            .remove([filePath]);

          if (storageError) {
            console.error("Dosya silinirken hata oluştu:", storageError);
          } else {
            console.log("Dosya başarıyla silindi:", filePath);
          }
        } catch (storageError) {
          console.error("Dosya silme hatası:", storageError);
          // Dosya silinemiyor olsa bile notu silmeye devam edebiliriz
        }
      }

      // Şimdi notu sil
      const { error: deleteError } = await supabase
        .from("task_notes")
        .delete()
        .eq("id", noteId);

      if (deleteError) throw deleteError;

      // Send delete notifications
      await notifyNoteDeleted(noteId, currentUser.id);

      // UI'da notları güncelle
      setNotes(notes.filter(note => note.id !== noteId));
      toast.success("Not ve ekleri başarıyla silindi");
    } catch (error) {
      console.error("Not silinirken hata oluştu:", error);
      toast.error("Not silinirken bir hata oluştu");
    }
  };

  // Dosya indirme fonksiyonu
  const handleDownloadFile = async (file) => {
    if (!file?.attachment_url) {
      toast.error("Dosya bağlantısı bulunamadı");
      return;
    }
    
    const toastId = toast.loading("Dosya indiriliyor... %0");
    
    try {
      // Doğru bucket adını kullan
      const bucketName = import.meta.env.VITE_FILE_SUPABASE_BUCKET_NAME;
      
      // Dosya URL'sini oluştur
      const fileUrl = `${import.meta.env.VITE_FILE_SUPABASE_URL}/storage/v1/object/public/${bucketName}/${file.attachment_url}`;
      
      // Dosyayı fetch et
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error(`Dosya indirilemedi: ${response.status} ${response.statusText}`);
      }

      // Toplam dosya boyutunu al
      const contentLength = response.headers.get('content-length');
      const total = parseInt(contentLength, 10);
      let loaded = 0;

      // Response body'yi ReadableStream olarak al
      const reader = response.body.getReader();
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        
        // İlerleme yüzdesini hesapla ve göster
        const progress = Math.round((loaded / total) * 100);
        toast.loading(`Dosya indiriliyor... %${progress}`, { id: toastId });
      }
      
      // Chunk'ları birleştir
      const blob = new Blob(chunks);
      
      // İndirme bağlantısı oluştur
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = file.attachment_name || 'download';
      
      // Dosyayı indir
      document.body.appendChild(a);
      a.click();
      
      // Temizlik
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Dosya başarıyla indirildi", { id: toastId });
    } catch (error) {
      console.error("Dosya indirme hatası:", error);
      toast.error("Dosya indirilirken bir hata oluştu", { id: toastId });
    }
  };

  const resetForm = () => {
    setNoteText("");
    setSelectedFile(null);
  };

  // Türkçe karakterleri ve özel karakterleri temizleme fonksiyonu ekleyelim
  const sanitizeFileName = (fileName) => {
    // Türkçe karakterleri değiştir
    const turkishChars = { 
      'ç': 'c', 'Ç': 'C', 
      'ğ': 'g', 'Ğ': 'G', 
      'ı': 'i', 'İ': 'I', 
      'ö': 'o', 'Ö': 'O', 
      'ş': 's', 'Ş': 'S', 
      'ü': 'u', 'Ü': 'U'
    };
    
    let sanitized = fileName;
    
    // Türkçe karakterleri değiştir
    Object.keys(turkishChars).forEach(char => {
      sanitized = sanitized.replace(new RegExp(char, 'g'), turkishChars[char]);
    });
    
    // Özel karakterleri kaldır (dosya sistemine zarar verebilecek karakterler)
    // Daha kapsamlı bir liste ile tüm problemli karakterleri kaldır - süslü parantezler dahil
    sanitized = sanitized.replace(/[*?:"<>|\\\/\{\},\(\)\[\]#%&~+=;@!$^]/g, '');
    
    // Boşlukları alt çizgi ile değiştir
    sanitized = sanitized.replace(/\s+/g, '_');
    
    // Uzun dosya adlarını kısalt (çok uzun isimler hata verebilir)
    if (sanitized.length > 50) {
      sanitized = sanitized.substring(0, 50);
    }
    
    // Dosya adını küçük harfe çevir
    sanitized = sanitized.toLowerCase();
    
    // Uyumsuz başlangıç karakterlerini kaldır (noktalar, alt çizgiler başta olmamalı)
    sanitized = sanitized.replace(/^[._-]+/, '');
    
    // Eğer tüm karakterler silinmişse, varsayılan bir isim kullan
    if (!sanitized) {
      sanitized = 'file';
    }
    
    // "teira3d_" ön ekini ekle
    return `teira3d_${sanitized}`;
  };

  // Dosya yükleme fonksiyonunu güncelleyelim
  const uploadFile = async (file, taskId) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Doğru bucket adını kullan
      const bucketName = import.meta.env.VITE_FILE_SUPABASE_BUCKET_NAME;
      
      // Benzersiz dosya adı oluştur
      const fileExt = file.name.split('.').pop();
      const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
      const sanitizedBaseName = sanitizeFileName(baseName);
      const timestamp = new Date().getTime();
      const randomString = Math.random().toString(36).substring(2, 8);
      const fileName = `${taskId}/${timestamp}_${randomString}_${sanitizedBaseName}.${fileExt}`;
      
      console.log('Sanitized file name:', fileName);
      
      // Simüle edilmiş ilerleme
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Supabase storage'a yükle
      const { data, error } = await fileSupabase.storage
        .from(bucketName)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true // Aynı isimde dosya varsa üzerine yaz
        });
      
      clearInterval(progressInterval);
      
      if (error) {
        console.log('Upload error details:', error);
        throw error;
      }
      
      // Yüklenen dosyanın public URL'ini al
      const { data: urlData } = fileSupabase.storage
        .from(bucketName)
        .getPublicUrl(fileName);
      
      setUploadProgress(100);
      
      return {
        url: urlData.publicUrl,
        name: file.name,
        size: file.size,
        path: data.path,
        type: file.type
      };
    } catch (error) {
      console.error("Dosya yüklenirken hata oluştu:", error);
      toast.error("Dosya yüklenirken bir hata oluştu: " + (error.message || error));
      throw error;
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 1000);
    }
  };

  // Dosya boyutunu formatla (bytes -> KB, MB)
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Not düzenleme modunu aç
  const startEditingNote = (note) => {
    // Yetki kontrolü - sadece not sahibi veya süper kullanıcı düzenleyebilir
    if (currentUser.id !== note.user_id && currentUser.role !== 'super') {
      toast.error("Bu notu düzenlemek için yetkiniz bulunmuyor");
      return;
    }
    
    setEditingNoteId(note.id);
    setEditNoteText(note.content);
    setEditFileToRemove(false);
    setEditNewFile(null);
  };

  // Not düzenleme modunu kapat
  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditNoteText("");
    setEditFileToRemove(false);
    setEditNewFile(null);
  };

  // Düzenleme modunda dosya seç
  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Dosya boyutu kontrolü (maksimum 128 GB)
    if (file.size > 128 * 1024 * 1024 * 1024) {
      toast.error("Dosya boyutu 128GB'tan büyük olamaz!");
      return;
    }
    
    setEditNewFile(file);
  };

  // Not güncelleme fonksiyonu
  const handleUpdateNote = async (noteId) => {
    try {
      setLoading(true);
      const noteToUpdate = notes.find(note => note.id === noteId);
      
      let updatedFileData = null;

      // Eğer yeni dosya yüklenecekse
      if (editNewFile) {
        // Eski dosyayı sil (eğer varsa)
        if (noteToUpdate.attachment_url) {
          const bucketName = import.meta.env.VITE_FILE_SUPABASE_BUCKET_NAME;
          await fileSupabase.storage
            .from(bucketName)
            .remove([noteToUpdate.attachment_url]);
        }
        
        // Yeni dosyayı yükle
        updatedFileData = await uploadFile(editNewFile, task.id);
      }
      
      // Eğer dosya kaldırılacaksa
      if (editFileToRemove && noteToUpdate.attachment_url) {
        const bucketName = import.meta.env.VITE_FILE_SUPABASE_BUCKET_NAME;
        await fileSupabase.storage
          .from(bucketName)
          .remove([noteToUpdate.attachment_url]);
      }

      // Notu güncelle
      const updateData = {
        content: editNoteText,
        updated_at: new Date().toISOString()
      };

      // Dosya bilgilerini güncelle
      if (editFileToRemove) {
        updateData.attachment_url = null;
        updateData.attachment_name = null;
        updateData.attachment_size = null;
      } else if (updatedFileData) {
        updateData.attachment_url = updatedFileData.path;
        updateData.attachment_name = updatedFileData.name;
        updateData.attachment_size = updatedFileData.size;
      }

      const { data, error } = await supabase
        .from("task_notes")
        .update(updateData)
        .eq("id", noteId)
        .select()
        .single();

      if (error) throw error;

      // Send update notifications
      await notifyNoteUpdated(data, currentUser.id);

      // Notlar listesini güncelle
      setNotes(notes.map(note => 
        note.id === noteId 
          ? { 
              ...note, 
              content: editNoteText,
              updated_at: new Date().toISOString(),
              attachment_url: editFileToRemove ? null : (updatedFileData ? updatedFileData.path : note.attachment_url),
              attachment_name: editFileToRemove ? null : (updatedFileData ? updatedFileData.name : note.attachment_name),
              attachment_size: editFileToRemove ? null : (updatedFileData ? updatedFileData.size : note.attachment_size)
            }
          : note
      ));

      // Düzenleme modunu kapat
      cancelEditingNote();
      toast.success("Not başarıyla güncellendi");

    } catch (error) {
      console.error("Not güncellenirken hata oluştu:", error);
      toast.error("Not güncellenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Devamını Gör/Gizle fonksiyonu
  const toggleNoteExpansion = (noteId) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  // Notları yenileme fonksiyonu
  const handleRefreshNotes = async () => {
    setIsRefreshing(true);
    try {
      await fetchTaskNotes();
      toast.success("Notlar güncellendi");
    } catch (error) {
      console.error("Notlar yenilenirken hata oluştu:", error);
      toast.error("Notlar yenilenirken bir hata oluştu");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filtreleme ve sıralama fonksiyonunu güncelleyelim
  const getFilteredAndSortedNotes = () => {
    let filtered = getFilteredNotes();

    // Arama filtresi
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(note => {
        const content = note.content?.toLowerCase() || '';
        const userName = `${note.user_profiles?.first_name} ${note.user_profiles?.last_name}`.toLowerCase();
        const fileName = note.attachment_name?.toLowerCase() || '';
        
        return content.includes(query) || 
               userName.includes(query) || 
               fileName.includes(query);
      });
    }

    // Sıralama
    switch (sortBy) {
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'user':
        filtered.sort((a, b) => {
          const nameA = `${a.user_profiles?.first_name} ${a.user_profiles?.last_name}`.toLowerCase();
          const nameB = `${b.user_profiles?.first_name} ${b.user_profiles?.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      default:
        break;
    }

    return filtered;
  };

  // Filtreleme fonksiyonu
  const getFilteredNotes = () => {
    let filtered = [...notes];

    // Tür filtreleme
    if (filterType === 'hasFile') {
      filtered = filtered.filter(note => note.attachment_url);
    } else if (filterType === 'edited') {
      filtered = filtered.filter(note => 
        note.updated_at && 
        note.created_at && 
        Math.abs(new Date(note.updated_at).getTime() - new Date(note.created_at).getTime()) > 1000
      );
    }

    // Kullanıcı filtreleme
    if (filterUser !== 'all') {
      filtered = filtered.filter(note => note.user_id === filterUser);
    }

    // Tarih filtreleme
    const now = new Date();
    if (filterDate === 'today') {
      filtered = filtered.filter(note => {
        const noteDate = new Date(note.created_at);
        return noteDate.toDateString() === now.toDateString();
      });
    } else if (filterDate === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(note => new Date(note.created_at) > weekAgo);
    } else if (filterDate === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(note => new Date(note.created_at) > monthAgo);
    }

    return filtered;
  };

  // Arama kısayolu için effect
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Command/Ctrl + K kısayolu
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Toggle note form visibility
  const toggleNoteForm = () => {
    setIsFormVisible(prev => !prev);
  };

  return (
    <div
      className="w-full md:w-96 lg:w-[496px] border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 overflow-y-auto overflow-x-hidden"
      style={{ maxHeight: "85vh"}}
    >
      <div className="p-6 h-full flex flex-col w-full">
        {/* Üst Başlık ve Bilgi Kısmı */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Görev Notları ve Raporlar
            </h3>
            <div className="flex items-center gap-2">
              {/* Form Toggle Button */}
              <button
                onClick={toggleNoteForm}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                title={isFormVisible ? "Not Ekleme Formunu Gizle" : "Not Ekleme Formunu Göster"}
              >
                {isFormVisible ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={handleRefreshNotes}
                disabled={isRefreshing || isLoading}
                className={`p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${isRefreshing ? 'cursor-not-allowed opacity-50' : ''}`}
                title="Notları Yenile"
              >
                <FiRefreshCw 
                  className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
                />
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Görevle ilgili notlarınızı ve raporlarınızı ekleyin.
          </p>
        </div>

        {/* Form section with animation */}
        <div
          ref={formRef}
          className="overflow-hidden transition-all duration-300"
          style={{
            maxHeight: isFormVisible ? '1000px' : '0px',
            opacity: isFormVisible ? 1 : 0,
            visibility: isFormVisible ? 'visible' : 'hidden',
            scrollbarWidth: 'none', /* Firefox */
            msOverflowStyle: 'none', /* IE and Edge */
          }}
        >
          {/* Note: Adding CSS for webkit scrollbars via inline styles */}
          <style dangerouslySetInnerHTML={{
            __html: `
              .hide-scrollbar::-webkit-scrollbar {
                display: none; /* Chrome, Safari and Opera */
              }
            `
          }} />
          
          {/* Not Ekleme Formu */}
          <form onSubmit={handleSubmit} className="mt-4 hide-scrollbar">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Not ekleyin..."
              className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-primary focus:border-primary dark:bg-gray-800 dark:text-white min-h-[100px] max-h-[350px]"
              rows={3}
            />
            
            {/* Dosya seçme alanı */}
            <div className="mt-3 space-y-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1 max-w-[272px]">
                    <FiFile className="w-3 h-3" />
                    Desteklenen Formatlar: PDF, DOCX, XLSX, JPG, PNG, ZIP vb.
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                  <FiLoader className="w-3 h-3 animate-spin text-green-700" />

                    Maksimum Boyut: 128GB
                  </span>
                </div>

                {/* Drag & Drop Zone */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      handleFileChange({ target: { files: [file] } });
                    }
                  }}
                  onClick={handleFileButtonClick}
                  className={`relative flex flex-col items-center justify-center p-6 transition-all duration-300 cursor-pointer group
                    ${isDragging 
                      ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-500 dark:border-primary-500' 
                      : 'bg-gray-50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-500'
                    } border-2 border-dashed rounded-xl`}
                >
                  <div className={`transition-transform duration-300 ${isDragging ? 'scale-110' : 'scale-100'}`}>
                    <div className="flex flex-col items-center gap-3">
                      <div className={`p-3 rounded-full transition-colors duration-300
                        ${isDragging 
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        <Upload className="w-6 h-6" />
                      </div>
                      <div className="text-center">
                        <p className={`text-sm font-medium mb-1 transition-colors duration-300
                          ${isDragging 
                            ? 'text-primary-600 dark:text-primary-400' 
                            : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {isDragging ? 'Dosyayı Bırakın' : 'Dosya Yüklemek için Tıklayın'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          veya dosyayı bu alana sürükleyin
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect Border */}
                  <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 pointer-events-none
                    ${isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                    ${isDragging ? 'border-primary-500' : 'border-primary-500/50'}
                    border-2 border-dashed`}
                  />
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-4 gap-y-1 px-1">
                  <span className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Türkçe karakter içeren dosya isimleri otomatik düzeltilecektir.
                  </span>
                </div>
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
              
              {/* Seçilen dosya bilgisi */}
              {selectedFile && (
                <div className="flex flex-col gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <FiFile className="text-primary flex-shrink-0" />
                    <span className="text-sm truncate flex-1 font-medium text-gray-700 dark:text-gray-300">
                      {selectedFile.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={resetForm}
                      disabled={isUploading}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
                    >
                      <FiX size={16} />
                    </Button>
                  </div>
                  
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <span>{formatFileSize(selectedFile.size)}</span>
                    <span>•</span>
                    <span>{selectedFile.type || "Bilinmeyen tür"}</span>
                  </div>

                  <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5 mt-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>Not başına en fazla 1 adet rapor dosyası yüklenebilir.</span>
                  </div>

                  {isUploading && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-300 font-medium">
                          Yükleniyor...
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {uploadProgress}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className="bg-primary h-full rounded-full transition-all duration-300 ease-out"
                          style={{ 
                            width: `${uploadProgress}%`,
                            background: uploadProgress < 100 
                              ? 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)' 
                              : '#10B981'
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <FiLoader className={`${uploadProgress < 100 ? "animate-spin" : "hidden"}`} size={12} />
                        <span>
                          {uploadProgress < 100 
                            ? "Dosya yükleniyor..." 
                            : "Yükleme tamamlandı!"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-3 text-right">
              <Button
                type="submit"
                disabled={loading || isUploading || (!noteText.trim() && !selectedFile)}
                className={`
                  inline-flex items-center justify-center gap-2 
                  px-4 py-2.5 
                  text-sm font-medium
                  text-white dark:text-gray-900
                  bg-gray-900 dark:bg-white
                  border border-transparent
                  rounded-lg
                  shadow-sm
                  transition-all duration-200
                  hover:bg-gray-800 dark:hover:bg-gray-100
                  focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 dark:focus:ring-gray-100
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-900 dark:disabled:hover:bg-white
                  ${loading || isUploading ? "cursor-wait" : ""}
                  min-w-[140px]
                `}
              >
                {loading || isUploading ? (
                  <div className="flex items-center gap-2">
                    <FiLoader className="w-4 h-4 animate-spin" />
                    <span>{isUploading ? "Yükleniyor..." : "Kaydediliyor..."}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    <span>Not Ekle</span>
                  </div>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Görev Tarihçesi Bölümü */}
        <div className="flex-1 overflow-y-auto relative">
          <div className="sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm z-10 pt-2 pb-3 border-b border-gray-200 dark:border-gray-700 mb-6 flex items-center justify-between shadow-sm z-50">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-500 dark:text-primary-400" />
              <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                Görev Raporları
              </h4>
            </div>
            <div className="flex items-center gap-3">
                {/* Yenile butonu
                              <button
                onClick={toggleTheme}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group"
                title={theme === 'dark' ? 'Açık Tema' : 'Koyu Tema'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                ) : (
                  <Moon className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                )}
              </button>
                */}
              <button
                onClick={() => handleRefreshNotes()}
                disabled={isRefreshing || isLoading}
                className={`p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group ${isRefreshing ? 'opacity-50' : ''}`}
                title="Yenile"
              >
                <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''} group-hover:scale-90 transition-transform`} />
              </button>
              <button
                onClick={() => setIsFullScreen(true)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group"
                title="Tam Ekran"
              >
                <Maximize2 className="w-5 h-5 group-hover:scale-90 transition-transform" />
              </button>
              <span className="text-xs font-medium bg-gray-100 dark:bg-gray-700 py-1 px-2 rounded-md text-gray-600 dark:text-gray-300">
                {new Date().toLocaleDateString("tr-TR")}
              </span>
            </div>
          </div>

          

          {/* Not İçeriği - Normal Görünüm */}
          <div className="px-1">
            {isLoading ? (
              <div className="relative ml-8 pl-8 border-l-2 border-gray-200 dark:border-gray-700">
                {[1, 2, 3].map((index) => (
                  <div 
                    key={index}
                    className="p-5 mb-6 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm relative mr-2 animate-pulse"
                  >
                    {/* Timeline avatar skeleton */}
                    <div className="absolute w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 -left-[52px] top-5"></div>
                    
                    {/* Content skeleton */}
                    <div className="flex flex-col gap-4">
                      {/* Header skeleton */}
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-2">
                          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                          <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        </div>
                      </div>
                      
                      {/* Content skeleton */}
                      <div className="space-y-2">
                        <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-3 w-[90%] bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-3 w-[75%] bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                      
                      {/* Attachment skeleton - randomly show for some items */}
                      {Math.random() > 0.5 && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded"></div>
                            <div className="flex-1">
                              <div className="h-3 w-32 bg-gray-200 dark:bg-gray-600 rounded mb-2"></div>
                              <div className="h-2 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                            </div>
                            <div className="w-16 h-6 bg-gray-200 dark:bg-gray-600 rounded"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : getFilteredAndSortedNotes().length === 0 ? (
              <div onClick={() => setIsFullScreen(true)}  className="py-10 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <AlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
                <p className="text-gray-600 dark:text-gray-300 font-medium">
                  Rapor bulunamadı
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-2 p-6">
                Şu an için oluşturulmuş bir rapor bulunmuyor. Detaylı filtreleme ve rapor oluşturma seçenekleri için tam ekran modunu kullanabilirsiniz.
                </p>
              </div>
            ) : (
              <div className="relative ml-8 pl-8 border-l-2 border-gray-200 dark:border-gray-700">
                {getFilteredAndSortedNotes().map((note, index) => (
                  <div 
                    key={note.id} 
                    className={`p-5 mb-6 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow relative mr-2`}
                  >
                    {/* Timeline avatar */}
                    <div className="absolute w-10 h-10 rounded-full overflow-hidden bg-white dark:bg-gray-800 border-2 border-primary-500 dark:border-primary-400 -left-[52px] top-5 z-10 shadow-md flex items-center justify-center">
                      {note.user_profiles?.profile_photo_url ? (
                        <img 
                          src={note.user_profiles.profile_photo_url} 
                          alt={`${note.user_profiles.first_name} ${note.user_profiles.last_name}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium">
                          {note.user_profiles?.first_name?.[0]}{note.user_profiles?.last_name?.[0]}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-3">
                      {/* Main content starts here, without the user avatar since it's now on the timeline */}
                      <div className="flex-1">
                        {/* Not Üst Bilgisi */}
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <span className="font-semibold text-gray-900 dark:text-white text-base">
                              {note.user_profiles?.first_name} {note.user_profiles?.last_name}
                              {currentUser?.id === note.user_id && (
                                <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 py-0.5 px-2 rounded-full">
                                  (Siz)
                                </span>
                              )}
                            </span>
                            <div className="mt-1">
                              <span className="text-xs bg-gray-100 dark:bg-gray-700 py-1 px-2 rounded text-gray-600 dark:text-gray-400 font-medium">
                                {new Date(note.created_at).toLocaleString("tr-TR", {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                          
                          {/* Silme butonu - sadece not sahibi için görünür */}
                          {(currentUser?.id === note.user_id || currentUser?.role === 'super') && (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditingNote(note)}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/20 p-2 rounded-lg"
                                disabled={editingNoteId === note.id}
                              >
                                <FiEdit size={18} />
                                {currentUser?.role === 'super' && currentUser?.id !== note.user_id && 
                                  <Shield className="w-3 h-3 ml-1 text-purple-500" />
                                }
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setIsDeleteModalOpen(true);
                                  setNoteToDelete(note.id);
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 p-2 rounded-lg"
                              >
                                <FiTrash2 size={18} />
                                {currentUser?.role === 'super' && currentUser?.id !== note.user_id && 
                                  <Shield className="w-3 h-3 ml-1 text-purple-500" />
                                }
                              </Button>
                            </div>
                          )}
                        </div>
                        
                        {/* Not İçeriği */}
                        {note.content && (
                          <>
                            {editingNoteId === note.id ? (
                              <div className="mb-4">
                                <textarea
                                  value={editNoteText}
                                  onChange={(e) => setEditNoteText(e.target.value)}
                                  className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-primary focus:border-primary dark:bg-gray-800 dark:text-white min-h-[150px] max-h-[320px]"
                                  rows={3}
                                  placeholder="Not içeriğini düzenleyin..."
                                />
                                
                                {/* Dosya Düzenleme Bölümü */}
                                <div className="mt-3 space-y-3">
                                  {note.attachment_url && !editFileToRemove && (
                                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                      <div className="flex items-center gap-2">
                                        <FiFile className="text-primary" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                          {note.attachment_name}
                                        </span>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditFileToRemove(true)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                      >
                                        <FiTrash2 size={16} />
                                      </Button>
                                    </div>
                                  )}

                                  {!note.attachment_url || editFileToRemove ? (
                                    <div>
                                      <input
                                        type="file"
                                        ref={editFileInputRef}
                                        onChange={handleEditFileChange}
                                        className="hidden"
                                      />
                                      {editNewFile ? (
                                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                          <div className="flex items-center gap-2">
                                            <FiFile className="text-primary" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                              {editNewFile.name}
                                            </span>
                                          </div>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setEditNewFile(null)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                          >
                                            <FiX size={16} />
                                          </Button>
                                        </div>
                                      ) : (
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => editFileInputRef.current?.click()}
                                          className="w-full flex items-center justify-center gap-2 py-2"
                                        >
                                          <FiPaperclip size={16} />
                                          <span>Dosya Ekle</span>
                                        </Button>
                                      )}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="mt-3 flex justify-end gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={cancelEditingNote}
                                    disabled={loading}
                                  >
                                    İptal
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => handleUpdateNote(note.id)}
                                    disabled={loading || (!editNoteText.trim() && !editNewFile && !note.attachment_url) || 
                                             (editNoteText.trim() === note.content && !editNewFile && !editFileToRemove)}
                                    className="bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                                  >
                                    {loading ? (
                                      <div className="flex items-center gap-2">
                                        <FiLoader className="animate-spin" size={16} />
                                        <span>Güncelleniyor...</span>
                                      </div>
                                    ) : (
                                      "Güncelle"
                                    )}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap mb-4 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg relative">
                                <div className={`${!expandedNotes.has(note.id) ? "max-h-[150px] overflow-hidden" : ""} relative`}>
                                  <div className="break-words overflow-x-hidden w-full">
                                    {note.content}
                                  </div>
                                  {!expandedNotes.has(note.id) && note.content.length > 300 && (
                                    <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 dark:from-gray-700/50 to-transparent rounded-[8px]"></div>
                                  )}
                                </div>
                                {note.content.length > 300 && (
                                  <button
                                    onClick={() => toggleNoteExpansion(note.id)}
                                    className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm flex items-center gap-1"
                                  >
                                    {expandedNotes.has(note.id) ? "Daha Az Göster" : "Devamını Gör"}
                                    <svg
                                      className={`w-4 h-4 transform transition-transform ${expandedNotes.has(note.id) ? "rotate-180" : ""}`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                )}
                                {note.updated_at && note.created_at && 
                                 Math.abs(new Date(note.updated_at).getTime() - new Date(note.created_at).getTime()) > 1000 && (
                                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                    (Düzenlendi: {new Date(note.updated_at).toLocaleString("tr-TR")})
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        
                        {/* Dosya Eki */}
                        {note.attachment_url && (
                          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-all hover:bg-gray-100 dark:hover:bg-gray-600">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-primary-500 flex-shrink-0">
                                  <FiFile size={20} className="text-primary-500 dark:text-primary-400" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 block truncate max-w-[172px]">
                                    {note.attachment_name}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                                    {formatFileSize(note.attachment_size)}
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDownloadFile(note)}
                                className="flex-shrink-0 bg-white dark:bg-gray-800 text-primary-500 hover:text-primary-600 border border-primary-200 hover:border-primary-300 dark:border-primary-800 dark:hover:border-primary-700 transition-colors group"
                              >
                                <FiDownload size={16} className="mr-1" />
                                <span className="text-xs font-medium">İndir</span>
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] transition-opacity duration-200">
          <div 
            className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl max-w-md w-full mx-4 transform transition-all duration-200 scale-100"
            onClick={(e) => e.stopPropagation()} 
          >
            <div className="text-center mb-5">
              <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <FiTrash2 size={24} className="text-red-500 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Not Silme İşlemi</h2>
              <p className="text-gray-600 dark:text-gray-300">Bu notu ve ekli dosyalarını kalıcı olarak silmek istediğinize emin misiniz?</p>
            </div>
            
            <div className="flex gap-3 justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 py-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                İptal
              </Button>
              <Button
                onClick={() => {
                  handleDeleteNote(noteToDelete);
                  setIsDeleteModalOpen(false);
                }}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                Evet, Sil
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tam Ekran Modal */}
      {isFullScreen && (
        <div className="fixed inset-0 bg-white dark:bg-gray-800 z-50 flex flex-col">
          {/* Modal Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/90 shadow-sm">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-primary-500 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Görev Tarihçesi
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group"
                title={theme === 'dark' ? 'Açık Tema' : 'Koyu Tema'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                ) : (
                  <Moon className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                )}
              </button>
              <button
                onClick={() => handleRefreshNotes()}
                disabled={isRefreshing || isLoading}
                className={`p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group ${isRefreshing ? 'opacity-50' : ''}`}
                title="Yenile"
              >
                <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''} group-hover:scale-90 transition-transform`} />
              </button>
              <button
                onClick={() => setIsFullScreen(false)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group"
                title="Tam Ekrandan Çık"
              >
                <Minimize2 className="w-5 h-5 group-hover:scale-90 transition-transform" />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-5xl mx-auto">
              {/* İstatistikler Bölümü */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Toplam Not</p>
                      <h4 className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                        {notes.length}
                      </h4>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <FileText className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Dosya Ekleri</p>
                      <h4 className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                        {notes.filter(note => note.attachment_url).length}
                      </h4>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <Paperclip className="w-6 h-6 text-green-500 dark:text-green-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Düzenlenen</p>
                      <h4 className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                        {notes.filter(note => 
                          note.updated_at && 
                          note.created_at && 
                          Math.abs(new Date(note.updated_at).getTime() - new Date(note.created_at).getTime()) > 1000
                        ).length}
                      </h4>
                    </div>
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                      <FiEdit className="w-6 h-6 text-yellow-500 dark:text-yellow-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Katkıda Bulunanlar</p>
                      <h4 className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                        {new Set(notes.map(note => note.user_id)).size}
                      </h4>
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <Users className="w-6 h-6 text-purple-500 dark:text-purple-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtreleme ve Sıralama Araçları */}
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Filtreleme ve Sıralama
                  </h3>
                  {/* Arama Kutusu */}
                  <div className="flex-1 max-w-md mx-4">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Notlarda ara..."
                        className="w-full pl-10 pr-12 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-400"
                      />
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <Search className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      </div>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute inset-y-0 right-3 flex items-center"
                        >
                          <X className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
                        </button>
                      )}
                      <div className="absolute right-8 top-4 -translate-y-1/2 px-1.5 py-0.5 rounded   ">
                        <kbd className="text-[10px] text-gray-400 dark:text-gray-500">⌘K</kbd>
                      </div>
                    </div>
                  </div>
                  {(filterType !== 'all' || filterUser !== 'all' || filterDate !== 'all' || searchQuery) && (
                    <button
                      onClick={() => {
                        setFilterType('all');
                        setFilterUser('all');
                        setFilterDate('all');
                        setSearchQuery('');
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <RefreshCcw className="w-4 h-4" />
                      <span>Filtreleri Temizle</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Tür Filtresi */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <FileType2 className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors" />
                    </div>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-400"
                    >
                      <option value="all">Tüm Notlar</option>
                      <option value="hasFile">Dosya Ekli</option>
                      <option value="edited">Düzenlenenler</option>
                    </select>
                  </div>

                  {/* Kullanıcı Filtresi */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <Users className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors" />
                    </div>
                    <select
                      value={filterUser}
                      onChange={(e) => setFilterUser(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-400"
                    >
                      <option value="all">Tüm Kullanıcılar</option>
                      {Array.from(new Set(notes.map(note => note.user_id))).map(userId => {
                        const user = notes.find(note => note.user_id === userId)?.user_profiles;
                        return (
                          <option key={userId} value={userId}>
                            {user ? `${user.first_name} ${user.last_name}` : 'Bilinmeyen Kullanıcı'}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Tarih Filtresi */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors" />
                    </div>
                    <select
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-400"
                    >
                      <option value="all">Tüm Zamanlar</option>
                      <option value="today">Bugün</option>
                      <option value="week">Son 7 Gün</option>
                      <option value="month">Son 30 Gün</option>
                    </select>
                  </div>

                  {/* Sıralama */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <ArrowUpDown className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors" />
                    </div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-400"
                    >
                      <option value="newest">En Yeni</option>
                      <option value="oldest">En Eski</option>
                      <option value="user">Kullanıcı</option>
                    </select>
                  </div>
                </div>

                {/* Aktif Filtreler */}
                {(filterType !== 'all' || filterUser !== 'all' || filterDate !== 'all' || searchQuery) && (
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Aktif Filtreler:</span>
                    {searchQuery && (
                      <span className="px-2 py-1 text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg flex items-center gap-1">
                        <Search className="w-3 h-3" />
                        Arama: {searchQuery}
                      </span>
                    )}
                    {filterType !== 'all' && (
                      <span className="px-2 py-1 text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg">
                        {filterType === 'hasFile' ? 'Dosya Ekli' : 'Düzenlenenler'}
                      </span>
                    )}
                    {filterUser !== 'all' && (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
                        {notes.find(note => note.user_id === filterUser)?.user_profiles?.first_name || 'Kullanıcı'}
                      </span>
                    )}
                    {filterDate !== 'all' && (
                      <span className="px-2 py-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg">
                        {filterDate === 'today' ? 'Bugün' : filterDate === 'week' ? 'Son 7 Gün' : 'Son 30 Gün'}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Notlar Listesi */}
              {isLoading ? (
                <div className="relative ml-8 pl-8 border-l-2 border-gray-200 dark:border-gray-700">
                  {[1, 2, 3].map((index) => (
                    <div 
                      key={index}
                      className="p-5 mb-6 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm relative mr-2 animate-pulse"
                    >
                      {/* Timeline avatar skeleton */}
                      <div className="absolute w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 -left-[52px] top-5"></div>
                      
                      {/* Content skeleton */}
                      <div className="flex flex-col gap-4">
                        {/* Header skeleton */}
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col gap-2">
                            <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                          </div>
                        </div>
                        
                        {/* Content skeleton */}
                        <div className="space-y-2">
                          <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                          <div className="h-3 w-[90%] bg-gray-200 dark:bg-gray-700 rounded"></div>
                          <div className="h-3 w-[75%] bg-gray-200 dark:bg-gray-700 rounded"></div>
                        </div>
                        
                        {/* Attachment skeleton - randomly show for some items */}
                        {Math.random() > 0.5 && (
                          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded"></div>
                              <div className="flex-1">
                                <div className="h-3 w-32 bg-gray-200 dark:bg-gray-600 rounded mb-2"></div>
                                <div className="h-2 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                              </div>
                              <div className="w-16 h-6 bg-gray-200 dark:bg-gray-600 rounded"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : getFilteredAndSortedNotes().length === 0 ? (
                <div  className="py-10 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <AlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
                  <p className="text-gray-600 dark:text-gray-300 font-medium">
                    Rapor bulunamadı 
                  </p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-2 animate-pulse">
                    Lütfen farklı filtre seçeneklerini deneyin
                  </p>
                </div>
              ) : (
                <div className="relative ml-8 pl-8 border-l-2 border-gray-200 dark:border-gray-700">
                  {getFilteredAndSortedNotes().map((note, index) => (
                    <div 
                      key={note.id} 
                      className={`p-5 mb-6 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow relative mr-2`}
                    >
                      {/* Timeline avatar */}
                      <div className="absolute w-10 h-10 rounded-full overflow-hidden bg-white dark:bg-gray-800 border-2 border-primary-500 dark:border-primary-400 -left-[52px] top-5 z-10 shadow-md flex items-center justify-center">
                        {note.user_profiles?.profile_photo_url ? (
                          <img 
                            src={note.user_profiles.profile_photo_url} 
                            alt={`${note.user_profiles.first_name} ${note.user_profiles.last_name}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium">
                            {note.user_profiles?.first_name?.[0]}{note.user_profiles?.last_name?.[0]}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-start gap-3">
                        {/* Main content starts here, without the user avatar since it's now on the timeline */}
                        <div className="flex-1">
                          {/* Not Üst Bilgisi */}
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <span className="font-semibold text-gray-900 dark:text-white text-base">
                                {note.user_profiles?.first_name} {note.user_profiles?.last_name}
                                {currentUser?.id === note.user_id && (
                                  <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 py-0.5 px-2 rounded-full">
                                    (Siz)
                                  </span>
                                )}
                              </span>
                              <div className="mt-1">
                                <span className="text-xs bg-gray-100 dark:bg-gray-700 py-1 px-2 rounded text-gray-600 dark:text-gray-400 font-medium">
                                  {new Date(note.created_at).toLocaleString("tr-TR", {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            </div>
                            
                            {/* Silme butonu - sadece not sahibi için görünür */}
                            {(currentUser?.id === note.user_id || currentUser?.role === 'super') && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => startEditingNote(note)}
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/20 p-2 rounded-lg"
                                  disabled={editingNoteId === note.id}
                                >
                                  <FiEdit size={18} />
                                  {currentUser?.role === 'super' && currentUser?.id !== note.user_id && 
                                    <Shield className="w-3 h-3 ml-1 text-purple-500" />
                                  }
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setIsDeleteModalOpen(true);
                                    setNoteToDelete(note.id);
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 p-2 rounded-lg"
                                >
                                  <FiTrash2 size={18} />
                                  {currentUser?.role === 'super' && currentUser?.id !== note.user_id && 
                                    <Shield className="w-3 h-3 ml-1 text-purple-500" />
                                  }
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {/* Not İçeriği */}
                          {note.content && (
                            <>
                              {editingNoteId === note.id ? (
                                <div className="mb-4">
                                  <textarea
                                    value={editNoteText}
                                    onChange={(e) => setEditNoteText(e.target.value)}
                                    className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-primary focus:border-primary dark:bg-gray-800 dark:text-white min-h-[150px] max-h-[320px]"
                                    rows={3}
                                    placeholder="Not içeriğini düzenleyin..."
                                  />
                                  
                                  {/* Dosya Düzenleme Bölümü */}
                                  <div className="mt-3 space-y-3">
                                    {note.attachment_url && !editFileToRemove && (
                                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <div className="flex items-center gap-2">
                                          <FiFile className="text-primary" />
                                          <span className="text-sm text-gray-700 dark:text-gray-300">
                                            {note.attachment_name}
                                          </span>
                                        </div>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setEditFileToRemove(true)}
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                        >
                                          <FiTrash2 size={16} />
                                        </Button>
                                      </div>
                                    )}

                                    {!note.attachment_url || editFileToRemove ? (
                                      <div>
                                        <input
                                          type="file"
                                          ref={editFileInputRef}
                                          onChange={handleEditFileChange}
                                          className="hidden"
                                        />
                                        {editNewFile ? (
                                          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <div className="flex items-center gap-2">
                                              <FiFile className="text-primary" />
                                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                                {editNewFile.name}
                                              </span>
                                            </div>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => setEditNewFile(null)}
                                              className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                            >
                                              <FiX size={16} />
                                            </Button>
                                          </div>
                                        ) : (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => editFileInputRef.current?.click()}
                                            className="w-full flex items-center justify-center gap-2 py-2"
                                          >
                                            <FiPaperclip size={16} />
                                            <span>Dosya Ekle</span>
                                          </Button>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="mt-3 flex justify-end gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={cancelEditingNote}
                                      disabled={loading}
                                    >
                                      İptal
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => handleUpdateNote(note.id)}
                                      disabled={loading || (!editNoteText.trim() && !editNewFile && !note.attachment_url) || 
                                               (editNoteText.trim() === note.content && !editNewFile && !editFileToRemove)}
                                      className="bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                                    >
                                      {loading ? (
                                        <div className="flex items-center gap-2">
                                          <FiLoader className="animate-spin" size={16} />
                                          <span>Güncelleniyor...</span>
                                        </div>
                                      ) : (
                                        "Güncelle"
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap mb-4 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg relative">
                                  <div className={`${!expandedNotes.has(note.id) ? "max-h-[150px] overflow-hidden" : ""} relative`}>
                                    <div className="break-words overflow-x-hidden w-full">
                                      {note.content}
                                    </div>
                                    {!expandedNotes.has(note.id) && note.content.length > 300 && (
                                      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 dark:from-gray-700/50 to-transparent rounded-[8px]"></div>
                                    )}
                                  </div>
                                  {note.content.length > 300 && (
                                    <button
                                      onClick={() => toggleNoteExpansion(note.id)}
                                      className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm flex items-center gap-1"
                                    >
                                      {expandedNotes.has(note.id) ? "Daha Az Göster" : "Devamını Gör"}
                                      <svg
                                        className={`w-4 h-4 transform transition-transform ${expandedNotes.has(note.id) ? "rotate-180" : ""}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                  )}
                                  {note.updated_at && note.created_at && 
                                   Math.abs(new Date(note.updated_at).getTime() - new Date(note.created_at).getTime()) > 1000 && (
                                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                      (Düzenlendi: {new Date(note.updated_at).toLocaleString("tr-TR")})
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          
                          {/* Dosya Eki */}
                          {note.attachment_url && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-all hover:bg-gray-100 dark:hover:bg-gray-600">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-primary-500 flex-shrink-0">
                                    <FiFile size={20} className="text-primary-500 dark:text-primary-400" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 block truncate">
                                      {note.attachment_name}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                                      {formatFileSize(note.attachment_size)}
                                    </span>
                                  </div>
                                </div>
                                <Button
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDownloadFile(note)}
                                  className="flex-shrink-0 bg-white dark:bg-gray-800 text-primary-500 hover:text-primary-600 border border-primary-200 hover:border-primary-300 dark:border-primary-800 dark:hover:border-primary-700 transition-colors group"
                                >
                                  <FiDownload size={16} className="mr-1" />
                                  <span className="text-xs font-medium">İndir</span>
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskNotes;
